import {
  getRail,
  getRide,
  getRoad,
  getWalk,
  type Mode,
  type Point,
  type Ride,
  type Road,
} from '@trippath/shared';
import { useEffect, useRef, useState } from 'react';

/** A way to ask the road of: by sea, along a ferry line or across the sea; on foot; or by rail. */
export interface Way {
  from: Point;
  to: Point;
  sea: boolean;
  foot?: boolean;
  rail?: boolean;
}

/**
 * A way between two points, by its ends rounded as the server keeps it — a walk's to about ten
 * metres, a drive's to about a hundred — and whether by sea, on foot or by rail.
 */
export function roadKey(
  from: Point,
  to: Point,
  sea: boolean,
  foot = false,
  rail = false,
): string {
  const at = (p: Point) => `${p.lat.toFixed(foot ? 4 : 3)},${p.lon.toFixed(foot ? 4 : 3)}`;
  return `${at(from)};${at(to)}${sea ? ' sea' : ''}${foot ? ' foot' : ''}${rail ? ' rail' : ''}`;
}

/** The key a way asked for is known by. */
export function keyOf(way: Way): string {
  return roadKey(way.from, way.to, way.sea, way.foot, way.rail);
}

/**
 * The road a trip's way of `mode` asks for: by sea for a ferry, on foot for a walk, its rails for
 * a train.
 */
export function wayAsk(from: Point, to: Point, mode: Mode | null | undefined): Way {
  return { from, to, sea: mode === 'ferry', foot: mode === 'foot', rail: mode === 'train' };
}

/** The key of the road a trip's way of `mode` goes along. */
export function wayKey(from: Point, to: Point, mode: Mode | null | undefined): string {
  return keyOf(wayAsk(from, to, mode));
}

/** What a way is asked of: its rails, its paths on foot, or its road. */
function ask(way: Way): Promise<Road | null> {
  if (way.rail) return getRail(way.from, way.to);
  return way.foot ? getWalk(way.from, way.to) : getRoad(way.from, way.to, way.sea);
}

/** The roads asked for so far on this page: `null` when there is none, or it could not be had. */
const known = new Map<string, Road | null>();
const asked = new Set<string>();
/** Each page drawing roads, told when one is answered — whenever it was asked, and by whom. */
const drawing = new Set<() => void>();

/** Forgets every road asked for: a test starts with none. */
export function forgetRoads(): void {
  known.clear();
  asked.clear();
}

/** An answer kept and drawn; a road not had is asked again when the ways next change. */
function settle(key: string, road: Road | null) {
  known.set(key, road);
  if (road === null) asked.delete(key);
  for (const redraw of drawing) redraw();
}

/**
 * The roads of these ways, each asked once a page: what is known now, filled in as the answers
 * come — also one asked for ways drawn before, so a route changed while its road was on the way
 * still gets it. A way not answered yet is not in it, and is drawn straight meanwhile.
 */
export function useRoads(ways: readonly Way[]): ReadonlyMap<string, Road | null> {
  const [roads, setRoads] = useState<ReadonlyMap<string, Road | null>>(() => new Map(known));
  // The ways as last drawn, read when which ways they are changes.
  const latest = useRef(ways);
  const keys = ways.map(keyOf).join('|');
  useEffect(() => {
    latest.current = ways;
  });

  useEffect(() => {
    const redraw = () => {
      setRoads(new Map(known));
    };
    drawing.add(redraw);
    return () => {
      drawing.delete(redraw);
    };
  }, []);

  useEffect(() => {
    for (const way of latest.current) {
      const key = keyOf(way);
      if (asked.has(key)) continue;
      asked.add(key);
      ask(way).then(
        (road) => {
          settle(key, road);
        },
        () => {
          settle(key, null);
        },
      );
    }
  }, [keys]);
  return roads;
}

/**
 * The road a way goes along, if it does: by bus or car the road found, by train its rails; by
 * ferry only a road that crosses on a ferry line, never one driven round by land.
 */
export function alongRoad(
  mode: Mode | null | undefined,
  road: Road | null | undefined,
): Road | null {
  if (!road) return null;
  if (mode === 'bus' || mode === 'car' || mode === 'train' || mode === 'foot') return road;
  return mode === 'ferry' && road.ferryKm > 0 ? road : null;
}

/** How long a way on the ground takes, in minutes, and whether that is only from its distance. */
export function groundTime(
  mode: 'train' | 'bus' | 'ferry' | 'car' | 'foot',
  km: number,
  road: Road | null | undefined,
): { minutes: number; rough: boolean } {
  const five = (minutes: number) => Math.max(5, Math.round(minutes / 5) * 5);
  if (road && mode === 'car') return { minutes: five(road.minutes), rough: false };
  // Along its rails: by its timetable, or as fast as its line allows and a sixth more.
  if (road && mode === 'train') return { minutes: five(road.minutes), rough: true };
  // A bus stops and keeps to its own speed: about a third longer than a car.
  if (road && mode === 'bus') return { minutes: five(road.minutes * 1.3), rough: true };
  // On a ferry line: its crossing as the router times it, the drive to and from the ports in it.
  if (road && mode === 'ferry') return { minutes: five(road.minutes), rough: true };
  // Without a road: the way on the ground is about a quarter longer than the straight line.
  const speed = { train: 90, bus: 55, ferry: 30, car: 75, foot: 4 }[mode];
  const along = mode === 'ferry' ? km : km * 1.25;
  return { minutes: five((along / speed) * 60), rough: true };
}

/** A ride across town to ask the way of: its ends, and when it leaves, an instant. */
export interface RideAsk {
  from: Point;
  to: Point;
  at: string;
}

/** A ride by its ends as the server keeps them, to about ten metres, and when it leaves. */
export function rideKey(from: Point, to: Point, at: string): string {
  return `${roadKey(from, to, false, true)} ${at}`;
}

/** The rides asked for so far on this page: `null` when there is none, or it could not be had. */
const rides = new Map<string, Ride | null>();
const ridesAsked = new Set<string>();
const riding = new Set<() => void>();

/** Forgets every ride asked for: a test starts with none. */
export function forgetRides(): void {
  rides.clear();
  ridesAsked.clear();
}

/** The rides of these asks, each asked once a page, filled in as the answers come. */
export function useRides(asks: readonly RideAsk[]): ReadonlyMap<string, Ride | null> {
  const [known, setKnown] = useState<ReadonlyMap<string, Ride | null>>(() => new Map(rides));
  const latest = useRef(asks);
  const keys = asks.map((ask) => rideKey(ask.from, ask.to, ask.at)).join('|');
  useEffect(() => {
    latest.current = asks;
  });

  useEffect(() => {
    const redraw = () => {
      setKnown(new Map(rides));
    };
    riding.add(redraw);
    return () => {
      riding.delete(redraw);
    };
  }, []);

  useEffect(() => {
    for (const ask of latest.current) {
      const key = rideKey(ask.from, ask.to, ask.at);
      if (ridesAsked.has(key)) continue;
      ridesAsked.add(key);
      const settled = (ride: Ride | null) => {
        rides.set(key, ride);
        for (const redraw of riding) redraw();
      };
      getRide(ask.from, ask.to, ask.at).then(settled, () => {
        ridesAsked.delete(key);
        settled(null);
      });
    }
  }, [keys]);
  return known;
}
